import { useState, useEffect } from 'react';
import {
  Modal,
  Backdrop,
  Box,
  Typography,
  IconButton,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import { Preference } from 'src/types/preference';
import { getPreferenceList, deletePreference } from 'src/apis/preference';
import useNotification from 'src/hooks/useNotification';
import { AlignmentType } from 'src/types/campaign';
import { PreferenceFormModal } from './PreferenceFormModal';

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      border: '1px solid #D0D5DD',
      borderRadius: 8,
      marginBottom: 12,
    },
    searchInput: {
      border: 'none',
      outline: 'none',
      flex: 1,
      fontSize: 14,
    },
    content: {
      width: '100%',
      maxHeight: 600,
      overflowY: 'auto',
      marginTop: 20,
    },
    buttons: {
      paddingTop: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      justifyContent: 'flex-end',
    },
    preferenceItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      border: '1px solid #EAECF0',
      borderRadius: 8,
      marginBottom: 8,
      cursor: 'pointer',
      '&:hover': {
        background: '#F9FAFB',
      },
    },
    preferenceName: {
      flex: 1,
    },
    actionButtons: {
      display: 'flex',
      gap: 8,
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 40,
      color: '#667085',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 40,
    },
  })
);

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 600,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

interface PreferencesModalProps {
  open: boolean;
  closeModal: () => void;
  onSelect?: (preferences: Preference[]) => void;
  selectedPreferenceIds?: string[];
  alignmentType?: AlignmentType;
  /** When true, only one preference can be selected at a time. */
  singleSelect?: boolean;
}

export const PreferencesModal = ({
  open,
  closeModal,
  onSelect,
  selectedPreferenceIds = [],
  alignmentType,
  singleSelect = false,
}: PreferencesModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { handleError, handleSuccess } = useNotification();

  const [preferences, setPreferences] = useState<Preference[]>([]);
  const [loading, setLoading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingPreference, setEditingPreference] = useState<Preference | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchKey, setSearchKey] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>(
    selectedPreferenceIds
  );

  useEffect(() => {
    if (open) {
      setSearchKey('');
      setSelectedIds(selectedPreferenceIds);
      loadPreferences();
    }
    // Sync selection from parent only when the modal opens.
  }, [open]);

  const filteredPreferences = preferences.filter(
    (preference) =>
      !searchKey ||
      preference.name.toLowerCase().includes(searchKey.toLowerCase())
  );

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const response = await getPreferenceList({ page: 0, pageSize: 1000 });
      setPreferences(response.preferences || []);
    } catch (error) {
      handleError(error, 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingPreference(null);
    setFormModalOpen(true);
  };

  const handleEdit = (preference: Preference) => {
    setEditingPreference(preference);
    setFormModalOpen(true);
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this preference?')) {
      return;
    }

    setDeletingId(id);
    try {
      await deletePreference(id);
      setPreferences(preferences.filter((p) => p.id !== id));
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
      handleSuccess('Preference deleted successfully');
    } catch (error) {
      handleError(error, 'Failed to delete preference');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggle = (preference: Preference) => {
    if (!onSelect) {
      return;
    }
    if (singleSelect) {
      setSelectedIds((prev) =>
        prev.includes(preference.id) ? [] : [preference.id]
      );
      return;
    }
    setSelectedIds((prev) =>
      prev.includes(preference.id)
        ? prev.filter((id) => id !== preference.id)
        : [...prev, preference.id]
    );
  };

  const handleConfirm = () => {
    if (!onSelect) {
      return;
    }
    const selected = preferences.filter((p) => selectedIds.includes(p.id));
    onSelect(selected);
  };

  const handleFormClose = () => {
    setFormModalOpen(false);
    setEditingPreference(null);
    loadPreferences();
  };

  return (
    <>
      <Modal
        open={open}
        aria-labelledby='preferences-modal-title'
        aria-describedby='preferences-modal-description'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Box sx={{ ...modalStyle }}>
          <Box className={classes.header}>
            <Typography
              className={textClasses.lgBold}
              style={{ color: '#101828' }}
            >
              Preferences
            </Typography>
            <CancelIcon
              cursor='pointer'
              width={24}
              height={24}
              onClick={closeModal}
            />
          </Box>
          <Box className={classes.searchContainer}>
            <SearchIcon style={{ color: '#667085' }} />
            <input
              type='text'
              placeholder='Search preferences...'
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              className={classes.searchInput}
            />
          </Box>
          <Box className={classes.content}>
            {loading ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress size={24} />
              </Box>
            ) : preferences.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography className={textClasses.smRegular}>
                  No preferences found. Create your first preference.
                </Typography>
              </Box>
            ) : filteredPreferences.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography className={textClasses.smRegular}>
                  No preferences match your search.
                </Typography>
              </Box>
            ) : (
              filteredPreferences.map((preference) => {
                const isSelected = selectedIds.includes(preference.id);
                return (
                  <Box
                    key={preference.id}
                    className={classes.preferenceItem}
                    onClick={() => handleToggle(preference)}
                    style={{
                      background: isSelected ? '#ECFDF3' : undefined,
                      borderColor: isSelected ? '#5EA1F2' : undefined,
                    }}
                  >
                    <Typography
                      className={`${textClasses.smBold} ${classes.preferenceName}`}
                      style={{ color: '#344054' }}
                    >
                      {preference.name}
                    </Typography>
                    <Box className={classes.actionButtons}>
                      <IconButton
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(preference);
                        }}
                        style={{ padding: 4 }}
                        title='Edit'
                      >
                        <EditIcon style={{ fontSize: 18, color: '#5EA1F2' }} />
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={(e) => handleDelete(preference.id, e)}
                        disabled={deletingId === preference.id}
                        style={{ padding: 4 }}
                        title='Delete'
                      >
                        {deletingId === preference.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon
                            style={{ fontSize: 18, color: '#B42318' }}
                          />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
          <Box className={classes.buttons}>
            <Button
              title='Cancel'
              variant='outlined'
              height={46}
              onClick={closeModal}
            />
            <Button title='Create New' height={46} onClick={handleCreateNew} />
            {onSelect && (
              <Button
                title={
                  singleSelect
                    ? 'Confirm'
                    : `Confirm${
                        selectedIds.length ? ` (${selectedIds.length})` : ''
                      }`
                }
                height={46}
                onClick={handleConfirm}
                disabled={singleSelect && selectedIds.length === 0}
              />
            )}
          </Box>
        </Box>
      </Modal>
      <PreferenceFormModal
        open={formModalOpen}
        closeModal={handleFormClose}
        preference={editingPreference}
        alignmentType={alignmentType}
      />
    </>
  );
};
